import type {
  ClientMessage,
  DifficultyTier,
  InputFrame,
  LobbyState,
  RoomPhase,
  ServerMessage,
  WorldState
} from "../shared/types";

export interface NetworkHandlers {
  onWelcome(playerId: string, phase: RoomPhase, state?: WorldState, lobby?: LobbyState): void;
  onLobby(lobby: LobbyState, phase: RoomPhase): void;
  onStart(state: WorldState): void;
  onSnapshot(state: WorldState): void;
  onError(message: string): void;
  onClose(): void;
}

export class RoomConnection {
  private socket: WebSocket | null = null;
  private sequence = 0;
  private connectGeneration = 0;
  private handlers: NetworkHandlers;
  playerId: string | null = null;
  latestState: WorldState | null = null;
  latestLobby: LobbyState | null = null;
  phase: RoomPhase = "lobby";

  constructor(
    private readonly roomCode: string,
    private readonly characterId: string,
    handlers: NetworkHandlers,
    private readonly displayName?: string
  ) {
    this.handlers = handlers;
  }

  setHandlers(handlers: NetworkHandlers): void {
    this.handlers = handlers;
  }

  connect(): void {
    const generation = ++this.connectGeneration;
    void this.connectAfterNameCheck(generation);
  }

  private async connectAfterNameCheck(generation: number): Promise<void> {
    const localDevelopment = location.hostname === "localhost" || location.hostname === "127.0.0.1";
    const socketOrigin = localDevelopment
      ? "ws://localhost:8787"
      : `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`;
    const httpOrigin = localDevelopment ? "http://localhost:8787" : location.origin;
    const params = new URLSearchParams({ character: this.characterId });
    if (this.displayName) params.set("name", this.displayName);

    if (this.displayName) {
      try {
        const availabilityUrl = new URL(
          `/api/rooms/${this.roomCode}/name-available`,
          httpOrigin
        );
        availabilityUrl.searchParams.set("name", this.displayName);
        const response = await fetch(availabilityUrl);
        if (response.ok) {
          const result = (await response.json()) as {
            available?: boolean;
            message?: string;
          };
          if (result.available === false) {
            this.handlers.onError(
              result.message ??
                "That name is already taken in this room. Choose a different name."
            );
            return;
          }
        }
      } catch {
        // The WebSocket connection below remains the source of truth if the
        // optional availability hint cannot be reached.
      }
    }

    if (generation !== this.connectGeneration) return;
    const url = `${socketOrigin}/api/rooms/${this.roomCode}/ws?${params}`;

    this.socket = new WebSocket(url);
    this.socket.addEventListener("message", (event) => this.handleMessage(String(event.data)));
    this.socket.addEventListener("error", () => this.handlers.onError("Could not reach the room server."));
    this.socket.addEventListener("close", () => this.handlers.onClose());
  }

  sendInput(input: InputFrame): void {
    this.send({ type: "input", seq: ++this.sequence, input });
  }

  selectCharacter(characterId: string): void {
    this.send({ type: "swap", characterId });
  }

  setReady(ready: boolean): void {
    this.send({ type: "ready", ready });
  }

  rename(displayName: string | null): void {
    this.send({ type: "rename", displayName });
  }

  continue(): void {
    this.send({ type: "continue" });
  }

  returnToLobby(): void {
    this.send({ type: "return" });
  }

  setDifficulty(tier: DifficultyTier): void {
    this.send({ type: "difficulty", tier });
  }

  close(): void {
    this.connectGeneration += 1;
    this.socket?.close(1000, "Player left");
    this.socket = null;
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private handleMessage(raw: string): void {
    try {
      const message = JSON.parse(raw) as ServerMessage;
      if (message.type === "welcome") {
        this.playerId = message.playerId;
        this.phase = message.phase;
        if (message.state) this.latestState = message.state;
        if (message.lobby) this.latestLobby = message.lobby;
        this.handlers.onWelcome(message.playerId, message.phase, message.state, message.lobby);
      } else if (message.type === "lobby") {
        this.phase = message.phase ?? "lobby";
        this.latestLobby = message.lobby;
        this.handlers.onLobby(message.lobby, this.phase);
      } else if (message.type === "start") {
        this.phase = "playing";
        this.latestState = message.state;
        this.handlers.onStart(message.state);
      } else if (message.type === "snapshot") {
        this.latestState = message.state;
        this.handlers.onSnapshot(message.state);
      } else if (message.type === "error") {
        this.handlers.onError(message.message);
      }
    } catch {
      this.handlers.onError("The room sent an invalid update.");
    }
  }
}
